import { useState } from 'react';
import { cancelarAbertura } from '../../api/aberturas.js';
import type { Abertura, AberturaCancelada } from '../../api/aberturas.js';
import { mensagemDoErro } from '../../api/erros.js';
import { CampoAreaTexto } from '../../componentes/Campo.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { LIMITE_DO_TEXTO } from './DialogoDeDecisao.js';
import { formatarQuantidade } from './quantidade.js';
import './DialogosDaAbertura.css';

type Props = {
  readonly abertura: Pick<Abertura, 'id' | 'produto' | 'entregue' | 'pendente'>;
  readonly aoCancelar: (cancelada: AberturaCancelada) => void;
  readonly aoVoltar: () => void;
};

/**
 * Cancelamento do saldo pendente (decisões de 2026-09-29).
 *
 * Corta só o que falta entregar: as entregas feitas continuam valendo e não
 * são desfeitas no ERP. Não há prazo automático — é sempre alguém do painel,
 * com motivo, e o motivo vai para a trilha. Sem motivo, o botão não age.
 */
export function DialogoDeCancelamento({ abertura, aoCancelar, aoVoltar }: Props) {
  const [motivo, definirMotivo] = useState('');
  const [agindo, definirAgindo] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  async function confirmar() {
    definirAgindo(true);
    definirErro(null);

    try {
      aoCancelar(await cancelarAbertura(abertura.id, motivo));
    } catch (falha: unknown) {
      definirErro(mensagemDoErro(falha));
      definirAgindo(false);
    }
  }

  return (
    <DialogoConfirmacao
      titulo={`Cancelar o saldo pendente de ${abertura.produto}?`}
      rotuloConfirmar="Cancelar saldo"
      rotuloCancelar="Voltar"
      agindo={agindo}
      confirmacaoBloqueada={motivo.trim() === ''}
      aoConfirmar={() => {
        void confirmar();
      }}
      aoCancelar={aoVoltar}
    >
      <p className="dialogo-abertura__risco">
        {formatarQuantidade(abertura.pendente)} deixam de existir: o operador não poderá mais
        entregar o que falta desta movimentação.
      </p>
      <p>
        As entregas feitas ({formatarQuantidade(abertura.entregue)}) continuam valendo e não são
        desfeitas no ERP.
      </p>
      <CampoAreaTexto
        rotulo="Motivo"
        obrigatorio
        valor={motivo}
        aoMudar={definirMotivo}
        maximoDeCaracteres={LIMITE_DO_TEXTO}
        desabilitado={agindo}
      />
      {erro !== null && <p className="dialogo-abertura__erro">{erro}</p>}
    </DialogoConfirmacao>
  );
}
